// ============================================================
// DetailCalcul — LA CHAÎNE, en clair.
// ------------------------------------------------------------
// Extrait de `PreChiffrage.jsx` le 8 septembre 2026, quand la chaîne a
// dû aller jusqu'au montant qui part sur l'offre.
//
// ── CE QU'ELLE DOIT FAIRE, ET C'EST TOUT ──
//
// Reconstruire chaque ligne à partir de la précédente, jusqu'au chiffre
// que le client verra. Une chaîne qui s'arrête avant la fin laisse deux
// nombres sans passerelle — c'est ce qui est arrivé au coût matière :
// 213,56 € ici, 234,92 € sur l'offre, sous le même mot.
// ============================================================

import { formatMesure, formatEuroOuTiret, formatNumber } from '../../lib/format.js';
import { ecartOffre } from './matiereOffre.js';

// « 0 kg » est apparu sur une pièce dont rien n'était calculable.


/**
 * LE CALCUL, en clair — une ligne toujours visible, le reste dépliable.
 *
 * « 108 cm³ » se croit ou ne se croit pas. « V = L × l × h : 90 × 60 ×
 * 20 mm = 108,0 cm³ » se vérifie de tête en trois secondes, et c'est
 * toute la différence entre un chiffre qu'on subit et un chiffre qu'on
 * contrôle — c'est aussi ce qui rend « pas de boîte noire » vérifiable
 * plutôt qu'affirmé.
 *
 * La chaîne complète — volume, masse, prix — est repliée : on la déroule
 * quand un montant surprend, pas à chaque coup d'œil.
 *
 * Chaque ligne RECONSTRUIT la suivante. Quand un format d'achat est
 * renseigné, on facture des barres entières et la masse achetée n'est
 * plus la masse des pièces : la chaîne le DIT, au lieu d'afficher une
 * multiplication qui ne retombe pas sur le total.
 */

/**
 * CE QUI PART SUR L'OFFRE, sous ce que Mertens a payé.
 *
 * La DÉCISION vit dans `matiereOffre.js`, pure et testée ; ici il ne
 * reste que le rendu.
 */
export function LigneOffre({ mat = {}, detail = null }) {
  const e = ecartOffre(mat, detail);
  if (!e) return null;
  if (e.texte) return <p className="field__hint">{e.texte}</p>;
  return (
    <p className="field__hint">
      Sur l’offre : <strong>{formatEuroOuTiret(e.montant)}</strong>
      {e.raison ? ` — ${e.raison}` : null}
    </p>
  );
}

export function DetailCalcul({ geo, mat, qty, ronde, detail = null }) {
  const c = geo?.cotes_brut ?? {};
  const vBrut = geo?.volume_brut_cm3;
  if (vBrut == null) return null;

  const formule = ronde
    ? `V = π × (Ø/2)² × L : π × (${formatMesure(c.diametre_mm)}/2)² × ${formatMesure(c.longueur_mm)} mm`
    : `V = L × l × h : ${formatMesure(c.longueur_mm)} × ${formatMesure(c.largeur_mm)} × ${formatMesure(c.epaisseur_mm)} mm`;

  const d = mat.density;
  const vPiece = mat.volume_cm3 ?? vBrut;
  const f = mat.from_format ?? null;

  return (
    <div className="calcul">
      <p className="calcul__ligne">
        {formule} = <strong>{formatMesure(vBrut, 'cm³')}</strong>
      </p>

      {/* Sans densité ni prix, il n'y a pas de chaîne à dérouler — et on
          ne déplie pas un vide. La raison est déjà dite au-dessus. */}
      {d != null ? (
        <details className="calcul__plus">
          <summary>Voir le détail du calcul</summary>
          <p className="calcul__ligne">
            Volume × masse vol. : {formatMesure(vPiece, 'cm³')} × {formatNumber(d)} g/cm³ ={' '}
            <strong>{formatMesure(mat.mass_per_piece_kg, 'kg')}</strong>/pc
          </p>

          {f ? (
            <p className="calcul__ligne">
              On achète la BARRE entière : {f.pieces_per_bar} pièce(s) par barre,{' '}
              {f.bars_needed} barre(s) pour {qty} → masse achetée{' '}
              <strong>{formatMesure(mat.purchased_mass_kg, 'kg')}</strong>, dont{' '}
              {formatMesure(f.unused_length_mm)} mm de chute.
            </p>
          ) : null}

          {mat.price_per_kg ? (
            <>
              <p className="calcul__ligne">
                Achat matière / pièce : {formatMesure(mat.mass_per_piece_kg, 'kg')} ×{' '}
                {formatEuroOuTiret(mat.price_per_kg)}/kg ={' '}
                <strong>
                  {formatEuroOuTiret(
                    mat.mass_per_piece_kg != null
                      ? Math.round(mat.mass_per_piece_kg * mat.price_per_kg * 100) / 100
                      : null,
                  )}
                </strong>
              </p>
              <p className="calcul__ligne">
                Achat matière (× {qty}) :{' '}
                <strong>{formatEuroOuTiret(mat.cost_calcule)}</strong>
                {mat.montant_retenu != null ? (
                  <> — remplacé par le montant retenu : {formatEuroOuTiret(mat.montant_retenu)}</>
                ) : null}
              </p>
              {/* Le dernier maillon : ce que le client verra. Une chaîne
                  qui s'arrête à l'achat laisse le lecteur devant deux
                  nombres sans passerelle. */}
              <LigneOffre mat={mat} detail={detail} />
            </>
          ) : null}
        </details>
      ) : null}
    </div>
  );
}

export default DetailCalcul;
